import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { Campaign, Store, Tag, User } from '@restore/services-lib';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { GET_CAMPAIGNS } from 'graphql/campaigns/queries/getCampaigns.gql';
import { CREATE_CAMPAIGN } from 'graphql/campaigns/mutations/createCampaign.gql';
import { UPDATE_CAMPAIGN } from 'graphql/campaigns/mutations/updateCampaign.gql';
import { ARCHIVE_CAMPAIGN } from 'graphql/campaigns/mutations/archiveCampaign.gql';
import { validateEntityName } from 'const/regex';
import { useAuthContext } from 'context/auth.context';
import { useSnackbarContext } from 'context/snackbar.context';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import { hasPermissions } from 'config/ability';

export interface CreateCampaignInterface {
  name: string;
  storeIds: number[];
  tagIds: number[];
  userIds: number[];
  startDate?: Date;
  endDate?: Date;
}

export interface UpdateCampaignInterface extends CreateCampaignInterface {
  id: number;
}

export interface CampaignInterface extends Campaign {
  stores: Store[];
  tags: Tag[];
  users: User[];
}

export interface CampaignsFilterInterface {
  tenantId?: number;
  userId?: number;
  userIds?: number[];
  name?: string;
  startDate?: Date;
  status?: string;
  storeIds?: number[];
  tagIds?: number[];
}

interface CampaignsContextInterface {
  campaigns: CampaignInterface[];
  setCampaigns: (campaigns: CampaignInterface[]) => void;
  campaignsFilter: CampaignsFilterInterface;
  setCampaignsFilter: (filter: CampaignsFilterInterface) => void;
  addCampaign: (campaign: CreateCampaignInterface) => Promise<void>;
  editCampaign: (campaign: UpdateCampaignInterface) => Promise<void>;
  archiveCampaign: (id: number) => Promise<void>;
  getCampaignById: (id: number) => CampaignInterface | undefined;
  getCampaignDropdownSelectOptionsByIds: (campaignIds: number[]) => DropdownOption[];
  refetchCampaigns: () => Promise<void>;
  loading: boolean;
  createCampaignLoading: boolean;
  updateCampaignLoading: boolean;
  archiveCampaignLoading: boolean;
  error?: ApolloError;
}

const defaultContext: CampaignsContextInterface = {
  campaigns: [],
  setCampaigns: () => {},
  campaignsFilter: {},
  setCampaignsFilter: () => {},
  addCampaign: async () => {},
  editCampaign: async () => {},
  archiveCampaign: async () => {},
  getCampaignById: () => undefined,
  getCampaignDropdownSelectOptionsByIds: () => [],
  loading: true,
  createCampaignLoading: false,
  updateCampaignLoading: false,
  archiveCampaignLoading: false,
  refetchCampaigns: async () => {},
};

const CampaignsContext = createContext<CampaignsContextInterface>(defaultContext);

interface CampaignsContextProviderInterface {
  children: ReactNode;
}

export const CampaignsContextProvider = ({ children }: CampaignsContextProviderInterface) => {
  const [campaigns, setCampaigns] = useState<CampaignInterface[]>([]);
  const [createCampaign, { loading: createCampaignLoading }] = useMutation(CREATE_CAMPAIGN);
  const [updateCampaign, { loading: updateCampaignLoading }] = useMutation(UPDATE_CAMPAIGN);
  const [archiveCampaignQuery, { loading: archiveCampaignLoading }] = useMutation(ARCHIVE_CAMPAIGN);
  const { ability, user, userTenantId } = useAuthContext();
  const { displaySnackbarMessage, displayApiResult } = useSnackbarContext();
  const [loading, setLoading] = useState<boolean>(hasPermissions(ability, 'read', 'Campaign'));
  const [campaignsFilter, setCampaignsFilter] = useState<CampaignsFilterInterface>({
    tenantId: userTenantId,
    // Set userId filter if current user does not have permissions to view all Campaigns in their tenant
    userId:
      !user || hasPermissions(ability, 'read', 'Campaign', { tenantId: userTenantId })
        ? undefined
        : user.user.id,
  });

  useMemo(() => {
    setCampaignsFilter({
      ...campaignsFilter,
      tenantId: userTenantId,
      userId:
        !user || hasPermissions(ability, 'read', 'Campaign', { tenantId: userTenantId })
          ? undefined
          : user.user.id,
    });
  }, [userTenantId, user, ability]);

  // fetch campaigns
  const { error, refetch } = useQuery(GET_CAMPAIGNS, {
    skip:
      !userTenantId || !campaignsFilter.tenantId || !hasPermissions(ability, 'read', 'Campaign'),
    variables: {
      campaignsFilter: campaignsFilter,
    },
    onCompleted: (data) => {
      setCampaigns(data.campaigns);
      setLoading(false);
    },
  });

  // this is an EXTRA check. validation is being handled upon name entry. in case that gets accidentally changed, this will prevent submission
  const handleValidateName = (name?: string | null) => {
    return !(!name || name.length < 2 || !validateEntityName(name));
  };

  const addCampaign = async (input: CreateCampaignInterface) => {
    if (handleValidateName(input.name)) {
      await createCampaign({
        variables: {
          input: {
            ...input,
            tenantId: userTenantId,
          },
        },
      }).then(
        (result) => {
          displayApiResult(result.data.createCampaign, 'Created successfully');
        },
        (rejection) => {
          displaySnackbarMessage(rejection.message, 'error');
        }
      );
      await refetch();
    }
  };

  // edit a campaign
  const editCampaign = async (input: UpdateCampaignInterface) => {
    await updateCampaign({
      variables: {
        input,
      },
    }).then(
      (result) => {
        displayApiResult(result.data.updateCampaign, 'Updated successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    await refetch();
  };

  // delete a campaign
  const archiveCampaign = async (id: number) => {
    await archiveCampaignQuery({
      variables: {
        id: Number(id),
      },
    }).then(
      (result) => {
        displayApiResult(result.data.archiveCampaign, 'Archived successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    await refetch();
  };

  // get campaign by id
  const getCampaignById = (id: number) => {
    return campaigns.find((campaign) => campaign.id === id);
  };

  // get campaign dropdown select options from an array of campaign ids
  const getCampaignDropdownSelectOptionsByIds = (campaignIds: number[]) => {
    const campaigns = campaignIds.map((id) => getCampaignById(id));
    const campaignDropdownOptions: DropdownOption[] = [];
    campaigns.forEach(
      (campaign) =>
        campaign && campaignDropdownOptions.push({ name: campaign.name, id: campaign.id })
    );
    return campaignDropdownOptions;
  };

  const refetchCampaigns = async () => {
    await refetch();
  };

  return (
    <CampaignsContext.Provider
      value={{
        campaigns,
        setCampaigns,
        campaignsFilter,
        setCampaignsFilter,
        addCampaign,
        editCampaign,
        archiveCampaign,
        getCampaignById,
        getCampaignDropdownSelectOptionsByIds,
        refetchCampaigns,
        loading,
        createCampaignLoading,
        updateCampaignLoading,
        archiveCampaignLoading,
        error,
      }}
    >
      {children}
    </CampaignsContext.Provider>
  );
};

export const useCampaignsContext = () => useContext(CampaignsContext);
